import { useMemo, useState } from "react";
import { Link } from "wouter";
import { useAuth } from "../../auth/AuthContext";
import { canManageFleet } from "../../auth/roleView";
import {
  useAgentSessions,
  useDeleteAgentSession,
  useLlmCredentials,
  useStopAgentSession,
} from "../../api/queries";
import type { AgentSession } from "../../api/types";
import { EmptyState } from "../../components/AsyncStates";
import { Button } from "../../components/Button";
import { DataTable, type DataTableColumn } from "../../components/DataTable";
import { PageHeader } from "../../components/PageHeader";
import { PaginatorControls, usePaginator } from "../../components/Paginator";
import { QueryStates } from "../../components/QueryStates";
import { AgentSessionStatusPill } from "../../components/StatePill";
import { useToast } from "../../toast/ToastContext";
import { LaunchWorkbenchWizard } from "./LaunchWorkbenchWizard";
import { DeleteSessionDialog } from "./DeleteSessionDialog";
import { StopSessionDialog } from "./StopSessionDialog";

export function AgentSessionsPage() {
  const { session } = useAuth();
  const { pushToast } = useToast();
  const paginator = usePaginator();
  const { data, isPending, isError, error, refetch } = useAgentSessions({
    cursor: paginator.cursor,
  });
  const { data: credentialsPage } = useLlmCredentials({ limit: 100 });
  const credentialsById = useMemo(
    () => new Map(credentialsPage?.items.map((c) => [c.id, c])),
    [credentialsPage],
  );
  const stopMutation = useStopAgentSession();
  const deleteMutation = useDeleteAgentSession();

  const [createOpen, setCreateOpen] = useState(false);
  const [stopTarget, setStopTarget] = useState<AgentSession | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<AgentSession | null>(null);

  const canManage = canManageFleet(session?.role);

  // Bonnie (local, worktree-per-session) sessions are the SAME underlying
  // resource but a materially different workflow (branch/dirty/promote, not
  // stop/delete) — they get their own dashboard (ADR-0039 P6, BonnieSessionsPage)
  // and are filtered out of this hosted-workbench view rather than shown
  // alongside sessions this page's Stop/Delete actions were built for.
  const hostedItems = data ? data.items.filter((s) => !s.local) : [];

  function handleStopConfirm() {
    if (!stopTarget) return;
    const { id, label } = stopTarget;
    stopMutation.mutate(
      { id },
      {
        onSuccess: () => {
          pushToast({ title: `Stopped "${label}".`, tone: "success" });
          setStopTarget(null);
        },
        onError: (err) => {
          pushToast({
            title: `Couldn't stop "${label}"`,
            description: err instanceof Error ? err.message : "Try again in a moment.",
            tone: "error",
          });
        },
      },
    );
  }

  function handleDeleteConfirm() {
    if (!deleteTarget) return;
    const { id, label } = deleteTarget;
    deleteMutation.mutate(
      { id },
      {
        onSuccess: () => {
          pushToast({ title: `Deleted "${label}".`, tone: "success" });
          setDeleteTarget(null);
        },
        onError: (err) => {
          pushToast({
            title: `Couldn't delete "${label}"`,
            description: err instanceof Error ? err.message : "Try again in a moment.",
            tone: "error",
          });
        },
      },
    );
  }

  const createButton = (
    <Button variant="primary" onClick={() => setCreateOpen(true)}>
      New session
    </Button>
  );

  // Cross-link to the other half of the (deliberately disjoint, see hostedItems'
  // comment above) sessions pair — the reverse of BonnieSessionsPage's own
  // "Hosted sessions →" link back here.
  const headerActions = (
    <>
      <Link to="/bonnie" className="button button--secondary">
        Local sessions (Bonnie) →
      </Link>
      {canManage && createButton}
    </>
  );

  const columns: DataTableColumn<AgentSession>[] = [
    { key: "label", header: "Label", cell: (s) => s.label, className: "data-table__primary" },
    { key: "status", header: "Status", cell: (s) => <AgentSessionStatusPill status={s.status} /> },
    { key: "image", header: "Image", cell: (s) => s.image, className: "data-table__secondary" },
    {
      key: "credential",
      header: "Credential",
      cell: (s) => (
        <span title={s.credentialId}>{credentialsById.get(s.credentialId)?.label ?? s.credentialId}</span>
      ),
      className: "data-table__secondary",
    },
    {
      key: "createdBy",
      header: "Started by",
      cell: (s) => s.createdBy || "—",
      className: "data-table__secondary",
    },
    ...(canManage
      ? [
          {
            key: "actions",
            header: <span className="visually-hidden">Actions</span>,
            cell: (s: AgentSession) => (
              <span style={{ display: "flex", gap: "var(--space-2)" }}>
                {(s.status === "pending" || s.status === "running") && (
                  <Button variant="secondary" onClick={() => setStopTarget(s)}>
                    Stop
                  </Button>
                )}
                <Button variant="danger" onClick={() => setDeleteTarget(s)}>
                  Delete
                </Button>
              </span>
            ),
          },
        ]
      : []),
  ];

  return (
    <>
      <PageHeader
        title="Workbench sessions"
        description="AI coding agent sessions running on your fleet. Each session ties an LLM credential to a Docker container."
        actions={headerActions}
      />

      <QueryStates
        isLoading={isPending}
        loadingLabel="Loading sessions…"
        isError={isError}
        error={error}
        onRetry={refetch}
        errorDescription={
          error instanceof Error
            ? error.message
            : "Couldn't load sessions. Check your connection and try again."
        }
        isEmpty={Boolean(data && hostedItems.length === 0)}
        empty={
          <EmptyState
            title="No workbench sessions"
            description="Start a session to spin up an AI coding agent on one of your fleet machines."
            action={canManage ? createButton : undefined}
          />
        }
      >
        {data && (
          <>
            <DataTable columns={columns} rows={hostedItems} rowKey={(s) => s.id} />
            <PaginatorControls
              itemCount={hostedItems.length}
              nextCursor={data.nextCursor}
              paginator={paginator}
              noun="sessions"
            />
          </>
        )}
      </QueryStates>

      {canManage && (
        <LaunchWorkbenchWizard open={createOpen} onClose={() => setCreateOpen(false)} />
      )}

      {canManage && (
        <StopSessionDialog
          session={stopTarget}
          isStopping={stopMutation.isPending}
          onCancel={() => setStopTarget(null)}
          onConfirm={handleStopConfirm}
        />
      )}

      {canManage && (
        <DeleteSessionDialog
          session={deleteTarget}
          isDeleting={deleteMutation.isPending}
          onCancel={() => setDeleteTarget(null)}
          onConfirm={handleDeleteConfirm}
        />
      )}
    </>
  );
}
